import React from 'react'
import { useMutation } from '@apollo/client'
import {
  CreateCompany as CreateCompanyType,
  CreateCompanyVariables,
  useAuth,
  UserPermissions,
} from '@modmd/data'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { useCompanyForm } from 'utils/form/companyForm'
import { DEVICE } from 'theme'
import Button from 'components/Button'
import Card from 'components/Card'
import Icon from 'components/Icon'
import { Box } from 'components/Layout'
import { Dialog } from 'components/Dialog'
import { CreateEditCompany } from 'internal-portal/routes/components/CreateEditCompany'
import { CREATE_COMPANY } from 'internal-portal/routes/components/CreateEditCompany/operations'
import { useToaster } from 'utils/useToaster'
import { GET_COMPANIES } from './operations'

export const PAGE_LENGTH = 10

export const NewCompany = () => {
  const { hasPermission } = useAuth()
  const { setToastMessage } = useToaster()
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [isCreateCompanyDialogOpen, setIsCreateCompanyDialogOpen] = React.useState(false)
  const [createCompany, { loading: isCreateCompanyLoading }] = useMutation<
    CreateCompanyType,
    CreateCompanyVariables
  >(CREATE_COMPANY, {
    refetchQueries: () => [
      {
        query: GET_COMPANIES,
        variables: {
          pagination: { from: 0, length: PAGE_LENGTH },
        },
      },
    ],
    awaitRefetchQueries: true,
  })
  const companyForm = useCompanyForm({
    validationSchema: 'fullCompany',
    onSubmit: async (values, formikHelpers) => {
      await createCompany({
        variables: {
          inputData: {
            ...values,
            name: values.name,
            Members: values.Members.map(({ User, role }) => ({
              userId: User.id!,
              role,
            })),
            Address: {
              street: values.Address.street!,
              city: values.Address.city!,
              country: values.Address.country!,
              county: values.Address.county,
              complement: values.Address.complement,
              state: values.Address.state!,
              zip: values.Address.zip!,
              lon: values.Address.lon,
              lat: values.Address.lat,
            },
          },
        },
      })
      formikHelpers.resetForm()
      setIsCreateCompanyDialogOpen(false)
      setToastMessage(`Company ${values.name} successfully added`, 'success')
    },
  })

  const handleCreateCompanyDialogOpen = React.useCallback(() => {
    setIsCreateCompanyDialogOpen(true)
  }, [])

  const handleCreateCompanyDialogClose = React.useCallback(() => {
    setIsCreateCompanyDialogOpen(false)
  }, [])

  return (
    <>
      {hasPermission([UserPermissions.CREATE_COMPANY]) && (
        <Button
          marginLeft="auto"
          onClick={handleCreateCompanyDialogOpen}
          leftIcon={<Icon.Plus size="L" />}
        >
          Create new
        </Button>
      )}
      <Dialog
        variant={isDesktop ? 'right' : 'fullscreen'}
        maxWidth={isDesktop ? '500px' : '100%'}
        isOpen={isCreateCompanyDialogOpen}
        onDismiss={handleCreateCompanyDialogClose}
      >
        <Box>
          <Card.Title onDismiss={handleCreateCompanyDialogClose}>New company</Card.Title>
          <Card.Content>
            <CreateEditCompany companyForm={companyForm} />
          </Card.Content>
          <Card.Actions>
            <Button isFetching={isCreateCompanyLoading} onClick={companyForm.submitForm}>
              Create new
            </Button>
          </Card.Actions>
        </Box>
      </Dialog>
    </>
  )
}
